import React, { useState, useEffect } from 'react';
import './App.css';
import API_BASE_URL from './config';

// Utility function to safely parse and format dates
function formatDateSafely(dateString) {
  if (!dateString || dateString === '' || dateString === '-') {
    return '-';
  }
  
  try {
    // Try to parse various date formats
    let date = null;
    
    // Try standard Date parsing first
    date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      // Try parsing common formats manually
      // Format: "Oct 24th 2025, 12:00 PM MST" or "11/05/2025 2:00 PM"
      const dateMatch = dateString.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (dateMatch) {
        // MM/DD/YYYY format
        date = new Date(dateMatch[3], dateMatch[1] - 1, dateMatch[2]);
      } else {
        // Try other parsing methods
        const monthMatch = dateString.match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})[a-z]{0,2}\s+(\d{4})/i);
        if (monthMatch) {
          const months = {Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11};
          const month = months[monthMatch[1]];
          const day = parseInt(monthMatch[2]);
          const year = parseInt(monthMatch[3]);
          date = new Date(year, month, day);
        }
      }
    }
    
    // If still invalid, return placeholder
    if (!date || isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    // Format as MM/DD/YYYY
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch (error) {
    console.error('Date parsing error:', error, 'for date:', dateString);
    return 'Invalid date';
  }
}

function ContactProfile({ contactId, onBack, onViewJob, theme, toggleTheme }) {
  const [contact, setContact] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [settings, setSettings] = useState({});
  const [cities, setCities] = useState([]);
  const [contactTypeSettings, setContactTypeSettings] = useState({});
  const [showEditForm, setShowEditForm] = useState(false);
  const [showInteractionForm, setShowInteractionForm] = useState(false);
  
  // Form states
  const [editData, setEditData] = useState({});
  const [newInteraction, setNewInteraction] = useState({
    interaction_type: 'General Note',
    interaction_text: '',
    user_name: 'User'
  });

  useEffect(() => {
    fetchContactDetails();
    fetchSettings();
    fetchCities();
    fetchContactTypeSettings();
  }, [contactId]);

  const fetchContactDetails = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/contact/${contactId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.contact) {
        setContact(data.contact);
        setEditData(data.contact);
        setAssignments(data.assignments || []);
        setInteractions(data.interactions || []);
      }
    } catch (error) {
      console.error('Error fetching contact details:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/contact_settings`, {
        credentials: 'include'
      });
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cities`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (Array.isArray(data)) {
        setCities(data);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchContactTypeSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/contact_type_settings`, {
        credentials: 'include'
      });
      const data = await response.json();
      setContactTypeSettings(data);
    } catch (error) {
      console.error('Error fetching contact type settings:', error);
    }
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_BASE_URL}/contact/${contactId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editData)
      });
      
      const result = await response.json();
      
      if (result.error) {
        alert(result.error);
      } else {
        setShowEditForm(false);
        fetchContactDetails();
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      alert('Failed to update contact');
    }
  };

  const handleAddInteraction = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_BASE_URL}/contact_interaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          contact_id: contactId,
          ...newInteraction
        })
      });
      
      const result = await response.json();
      
      if (result.error) {
        alert(result.error);
      } else {
        setShowInteractionForm(false);
        setNewInteraction({
          interaction_type: 'General Note',
          interaction_text: '',
          user_name: 'User'
        });
        fetchContactDetails();
      }
    } catch (error) {
      console.error('Error adding interaction:', error);
      alert('Failed to add interaction');
    }
  };

  const handleDeleteContact = async () => {
    if (!window.confirm(`Are you sure you want to delete ${contact.name}? This will also delete all assignments and interactions.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/contact/${contactId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (result.error) {
        alert(result.error);
      } else {
        alert('Contact deleted successfully');
        onBack();
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Failed to delete contact');
    }
  };

  const getStatusBadgeColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'won': return 'success';
      case 'confirmed': return 'info';
      case 'proposed': return 'warning';
      case 'lost': return 'danger';
      default: return 'secondary';
    }
  };

  if (!contact) {
    return <div className="container mt-4">Loading...</div>;
  }

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <button className="btn btn-outline-secondary me-3" onClick={onBack}>
            ← Back to Contacts
          </button>
        </div>
        <div>
          <button className="btn btn-secondary me-2" onClick={toggleTheme}>
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
          <button 
            className="btn btn-primary me-2" 
            onClick={() => setShowEditForm(!showEditForm)}
          >
            ✏️ Edit Contact
          </button>
          <button 
            className="btn btn-danger" 
            onClick={handleDeleteContact}
          >
            🗑️ Delete
          </button>
        </div>
      </div>

      {/* Contact Info Card */}
      <div className="card mb-4">
        <div className="card-body">
          <h2 className="card-title mb-3">{contact.name}</h2>
          
          <div className="row">
            <div className="col-md-6">
              <p><strong>Company:</strong> {contact.company_name || '-'}</p>
              <p><strong>Type:</strong> {contact.contact_type ? (
                <span className="badge bg-secondary ms-2">{contact.contact_type}</span>
              ) : '-'}</p>
              {contact.associated_city && (
                <p><strong>Associated City:</strong> <span className="badge bg-info ms-2">{contact.associated_city}</span></p>
              )}
            </div>
            <div className="col-md-6">
              <p><strong>Phone:</strong> {contact.phone ? (
                <a href={`tel:${contact.phone}`}>{contact.phone}</a>
              ) : '-'}</p>
              <p><strong>Email:</strong> {contact.email ? (
                <a href={`mailto:${contact.email}`}>{contact.email}</a>
              ) : '-'}</p>
            </div>
          </div>
          
          {contact.notes && (
            <div className="mt-3">
              <strong>Notes:</strong>
              <p className="mt-2">{contact.notes}</p>
            </div>
          )}
          
          <div className="mt-3 text-muted">
            <small>
              Added: {new Date(contact.created_at).toLocaleString()} | 
              Updated: {new Date(contact.updated_at).toLocaleString()}
            </small>
          </div>
        </div>
      </div>

      {/* Edit Form */}
      {showEditForm && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Edit Contact Information</h5>
            <form onSubmit={handleUpdateContact}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editData.name || ''}
                    onChange={(e) => setEditData({...editData, name: e.target.value})}
                  />
                </div>
                
                <div className="col-md-6 mb-3">
                  <label className="form-label">Company Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editData.company_name || ''}
                    onChange={(e) => setEditData({...editData, company_name: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Contact Type</label>
                  <select
                    className="form-select"
                    value={editData.contact_type || ''}
                    onChange={(e) => setEditData({...editData, contact_type: e.target.value})}
                  >
                    <option value="">Select type...</option>
                    {(settings.contact_types || []).map((type, idx) => (
                      <option key={idx} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                {/* Conditional City Dropdown */}
                {editData.contact_type && contactTypeSettings[editData.contact_type] && (
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Associated City *</label>
                    <select
                      className="form-select"
                      value={editData.associated_city || ''}
                      onChange={(e) => setEditData({...editData, associated_city: e.target.value})}
                      required
                    >
                      <option value="">Select city...</option>
                      {cities.map((city, idx) => (
                        <option key={idx} value={city.name}>{city.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div className="col-md-4 mb-3">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={editData.phone || ''}
                    onChange={(e) => setEditData({...editData, phone: e.target.value})}
                  />
                </div>
                
                <div className="col-md-4 mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={editData.email || ''}
                    onChange={(e) => setEditData({...editData, email: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="mb-3">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={editData.notes || ''}
                  onChange={(e) => setEditData({...editData, notes: e.target.value})}
                />
              </div>
              
              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary">Save Changes</button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEditForm(false);
                    setEditData(contact);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Job Assignments Section */}
      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">📋 Job Assignments ({assignments.length})</h5>
          
          {assignments.length === 0 ? (
            <p className="text-muted">No job assignments yet.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Organization</th>
                    <th>Due Date</th>
                    <th>Capacity</th>
                    <th>Status</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((assignment) => (
                    <tr 
                      key={assignment.id}
                      onClick={() => assignment.job_id && onViewJob(assignment.job_id)}
                      style={{ cursor: assignment.job_id ? 'pointer' : 'default' }}
                    >
                      <td>
                        <strong>{assignment.title || 'Unknown Job'}</strong>
                        {assignment.job_id && (
                          <div className="text-muted small">{assignment.job_id.substring(0, 8)}</div>
                        )}
                      </td>
                      <td>{assignment.organization || '-'}</td>
                      <td>{formatDateSafely(assignment.due_date)}</td>
                      <td>
                        {assignment.capacity && (
                          <span className="badge bg-info">{assignment.capacity}</span>
                        )}
                      </td>
                      <td>
                        {assignment.status && (
                          <span className={`badge bg-${getStatusBadgeColor(assignment.status)}`}>
                            {assignment.status}
                          </span>
                        )}
                      </td>
                      <td className="small">{assignment.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Interaction Timeline Section */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title mb-0">💬 Interaction Timeline ({interactions.length})</h5>
            <button 
              className="btn btn-sm btn-primary"
              onClick={() => setShowInteractionForm(!showInteractionForm)}
            >
              + Add Note
            </button>
          </div>
          
          {/* Add Interaction Form */}
          {showInteractionForm && (
            <div className="card bg-light mb-3">
              <div className="card-body">
                <form onSubmit={handleAddInteraction}>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Interaction Type</label>
                      <select
                        className="form-select"
                        value={newInteraction.interaction_type}
                        onChange={(e) => setNewInteraction({
                          ...newInteraction, 
                          interaction_type: e.target.value
                        })}
                      >
                        {(settings.interaction_types || []).map((type, idx) => (
                          <option key={idx} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="col-md-8 mb-3">
                      <label className="form-label">Your Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newInteraction.user_name}
                        onChange={(e) => setNewInteraction({
                          ...newInteraction, 
                          user_name: e.target.value
                        })}
                      />
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Note / Details</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={newInteraction.interaction_text}
                      onChange={(e) => setNewInteraction({
                        ...newInteraction, 
                        interaction_text: e.target.value
                      })}
                      required
                    />
                    <small className="text-muted">
                      Future: Smart tagging with @job and @city will be added in a future update
                    </small>
                  </div>
                  
                  <div className="d-flex gap-2">
                    <button type="submit" className="btn btn-primary">Add Interaction</button>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => setShowInteractionForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
          
          {/* Timeline */}
          {interactions.length === 0 ? (
            <p className="text-muted">No interactions recorded yet.</p>
          ) : (
            <div className="timeline">
              {interactions.map((interaction) => (
                <div key={interaction.id} className="journal-entry mb-3 p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <span className="badge bg-info me-2">{interaction.interaction_type}</span>
                      <strong>{interaction.user_name}</strong>
                    </div>
                    <small className="text-muted">
                      {new Date(interaction.created_at).toLocaleString()}
                    </small>
                  </div>
                  <p className="mb-0">{interaction.interaction_text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ContactProfile;

